import { ArrowDown, ArrowUp } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Direction indicator.
 *
 * Colour alone is not an accessible encoding, so every instance pairs the hue with
 * an arrow glyph and a screen-reader word.
 */
export function Direction({
  dir,
  children,
  size = 13,
}: {
  dir: "up" | "down";
  children?: ReactNode;
  size?: number;
}) {
  const Icon = dir === "up" ? ArrowUp : ArrowDown;
  return (
    <span className={`row ${dir}`} style={{ display: "inline-flex", gap: 4 }}>
      <Icon size={size} aria-hidden="true" />
      <span className="sr-only">{dir === "up" ? "up" : "down"}</span>
      {children}
    </span>
  );
}

export function ChangeCell({ pct }: { pct: number | null | undefined }) {
  if (pct === null || pct === undefined) return <span className="dim">—</span>;
  return (
    <Direction dir={pct >= 0 ? "up" : "down"}>
      <span className="num">{Math.abs(pct).toFixed(2)}%</span>
    </Direction>
  );
}

/** Probability bar with real progressbar semantics. */
export function Meter({
  value,
  dir,
  label,
}: {
  value: number;
  dir?: "up" | "down";
  label: string;
}) {
  const clamped = Math.min(Math.max(value, 0), 1);
  return (
    <div
      className="meter"
      role="progressbar"
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className={`meter-fill ${dir ?? ""}`} style={{ width: `${clamped * 100}%` }} />
    </div>
  );
}

export function Badge({
  tone = "neutral",
  children,
  title,
}: {
  tone?: "up" | "down" | "warn" | "neutral" | "accent";
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className={`badge ${tone}`} title={title}>
      {children}
    </span>
  );
}

export function Skeleton({
  w = "100%",
  h = 14,
  radius,
}: {
  w?: string | number;
  h?: string | number;
  radius?: number;
}) {
  return (
    <div
      className="skeleton"
      style={{ width: w, height: h, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/** Table placeholder that keeps layout stable while the first payload lands. */
export function SkeletonRows({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <Skeleton w={c === 0 ? "60%" : "80%"} h={13} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="empty" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <p className="muted small">{label}</p>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon">{icon}</div>}
      <p style={{ fontWeight: 600, color: "var(--text)" }}>{title}</p>
      {children && <p className="small muted">{children}</p>}
    </div>
  );
}

/** Banners carry role="alert" so a screen reader announces failures and fills. */
export function Banner({
  tone,
  icon,
  children,
}: {
  tone: "error" | "success" | "warn" | "info";
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`banner ${tone}`} role={tone === "error" ? "alert" : "status"}>
      {icon}
      <div>{children}</div>
    </div>
  );
}

/** Live/stale data indicator — the app used to show cached prices as if live. */
export function LiveDot({
  stale,
  ageSeconds,
}: {
  stale: boolean;
  ageSeconds?: number | null;
}) {
  const label = stale
    ? `Prices may be stale${ageSeconds ? ` (updated ${Math.round(ageSeconds)}s ago)` : ""}`
    : "Prices are live";
  return (
    <span className="row small dim" title={label}>
      <span className={`live-dot${stale ? " stale" : ""}`} aria-hidden="true" />
      <span className="sr-only">{label}</span>
      <span aria-hidden="true">{stale ? "delayed" : "live"}</span>
    </span>
  );
}
